import { onlyText } from "react-children-utilities";
import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import Head from "next/head";
import { MDXRemote } from "next-mdx-remote";
import { serialize } from "next-mdx-remote/serialize";
import { ParsedUrlQuery } from "querystring";
import remarkGfm from "remark-gfm";
import { components } from "components/atoms/MDX";
import { Prose } from "components/atoms/Prose";
import BlogNavigation from "components/molecules/BlogNavigation";
import { BlogPage } from "components/organisms/BlogPage";
import { MDXFrontMatter } from "types";
import { getAllMdx } from "utils/mdx";
interface ContextProps extends ParsedUrlQuery {
  slug: string;
}
interface BlogPostProps {
  frontMatter: MDXFrontMatter;
  mdx: any;
  previous: MDXFrontMatter | null;
  next: MDXFrontMatter | null;
}
const BlogPost: NextPage<BlogPostProps> = ({
  frontMatter,
  mdx,
  previous,
  next
}) => {
  return <>
            <Head data-sentry-element="Head" data-sentry-source-file="index.tsx">
                <title>{`CharGen: ${onlyText(frontMatter.title)}`}</title>
                <meta name="description" content={onlyText(frontMatter.description)} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="og:url" key="og:url" content={`https://char-gen.com/blogs/${frontMatter.slug}`} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="og:description" key="og:description" content={onlyText(frontMatter.description)} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
            </Head>
            <BlogPage {...frontMatter} data-sentry-element="BlogPage" data-sentry-source-file="index.tsx">
                <Prose data-sentry-element="Prose" data-sentry-source-file="index.tsx">
                    <MDXRemote {...mdx} components={components} data-sentry-element="MDXRemote" data-sentry-source-file="index.tsx" />
                </Prose>
                <BlogNavigation previous={previous || undefined} next={next || undefined} data-sentry-element="BlogNavigation" data-sentry-source-file="index.tsx" />
            </BlogPage>
        </>;
};
export const getStaticPaths: GetStaticPaths = async () => {
  const mdxFiles = getAllMdx();
  return {
    paths: mdxFiles.map(file => ({
      params: {
        slug: file?.frontMatter?.slug
      }
    })),
    fallback: false
  };
};
export const getStaticProps: GetStaticProps = async context => {
  const {
    slug
  } = context.params as ContextProps;
  const mdxFiles = getAllMdx();
  const blogIndex = mdxFiles.findIndex((p: {
    frontMatter: {
      slug: string;
    };
  } | null) => p?.frontMatter?.slug === slug);
  const blog = mdxFiles[blogIndex];
  const {
    frontMatter,
    content
  } = blog || {
    frontMatter: null,
    content: ""
  };
  const mdxContent = await serialize(content, {
    mdxOptions: {
      remarkPlugins: [remarkGfm]
    },
    scope: frontMatter || {}
  });
  return {
    props: {
      frontMatter,
      mdx: mdxContent,
      previous: blogIndex > 0 ? mdxFiles[blogIndex - 1]?.frontMatter : null,
      next: blogIndex < mdxFiles.length - 1 ? mdxFiles[blogIndex + 1]?.frontMatter : null
    }
  };
};
export default BlogPost;